import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronRight, FaSearch } from 'react-icons/fa';

import Drawer from '../../components/Tools/DrawerCustom';
import DetailRemboursement from './DetailRemboursement';
import { formaterMois, formaterMontant } from './formatAvances';
import { avecRemboursement, resumer } from './remboursements';

/**
 * Le tableau des remboursements en cours, commun au salarié et à la RH.
 *
 * Une ligne par remboursement : qui et quoi, la mensualité, la progression, le
 * reste dû et la prochaine échéance ; le détail des mois s'ouvre dans un
 * panneau latéral. C'est la forme des SIRH du marché, et la seule qui passe à
 * l'échelle — des cartes empilées, une par prêt, se répètent et ne se lisent
 * plus au-delà de quelques-unes (recette du 21/09/2026).
 *
 * Une seule structure pour deux mises en page : des colonnes sur grand écran,
 * des lignes empilées avec leurs libellés sur téléphone. Rendre deux arbres, un
 * par taille, doublerait chaque élément pour les lecteurs d'écran.
 *
 * Côté RH (`outils`), ce qu'il faut pour des dizaines de lignes : chercher un
 * salarié, filtrer par type, trier, paginer par dix, et le total de l'encours
 * de ce qu'on regarde.
 */

const PAR_PAGE = 10;
const TYPES = ['tous', 'avance', 'pret', 'acompte'];
const TRIS = ['echeance', 'reste', 'nom'];

// Littérale pour que Tailwind la génère. Cinq colonnes : la première porte
// l'identité de la ligne sur trois niveaux — qui ou quoi, le montant, la
// mensualité —, ce qui tient dès 768 px, y compris avec le menu de l'espace à
// gauche. Une colonne de plus pour la mensualité forçait le seuil à 1024 px, et
// l'écran d'ordinateur ordinaire retombait dans la vue empilée.
const GRILLE = 'md:grid-cols-[minmax(0,1.8fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1.1fr)_auto]';

/** Minuscules et sans accents : « diédhiou » trouve « DIEDHIOU ». */
function normaliser(texte) {
  return (texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function Cellule({ libelle, children, className = '' }) {
  return (
    <div role="cell" className={`min-w-0 ${className}`}>
      <span className="block text-xs text-slate-500 md:hidden">{libelle}</span>
      {children}
    </div>
  );
}

function LigneRemboursement({ demande, locale, prefixe, afficherDemandeur, onVoir }) {
  const { t } = useTranslation();
  const resume = resumer(demande);
  const type = t(`mesAvances.types.${demande.type}`, { defaultValue: demande.type });

  return (
    <div
      role="row"
      data-testid={`${prefixe}-remboursement-${demande.id}`}
      className={`grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-3 px-4 py-3 sm:px-5 md:items-center md:gap-y-0 hover:bg-slate-50/70 transition-colors ${GRILLE}`}
    >
      <div role="cell" className="min-w-0">
        <p className="font-medium text-slate-900 truncate" title={afficherDemandeur ? demande.demandeur?.nom : type}>
          {afficherDemandeur ? demande.demandeur?.nom || '—' : type}
        </p>
        <p className="text-sm text-slate-500 tabular-nums">
          {afficherDemandeur ? `${type} · ` : ''}
          {formaterMontant(resume.montant, locale)}
        </p>
        <p className="text-xs text-slate-500 tabular-nums">
          {t('mesAvances.parMois', { montant: formaterMontant(resume.mensualite, locale) })}
        </p>
      </div>

      <Cellule libelle={t('mesAvances.colonnes.progression')} className="col-span-2 md:col-span-1 md:px-2">
        {resume.pourcentage !== null ? (
          <>
            <div className="flex items-center gap-2">
              <div
                role="progressbar"
                aria-valuenow={resume.pourcentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('mesAvances.progression', { pourcentage: resume.pourcentage })}
                className="h-1.5 flex-1 rounded-full bg-slate-200 overflow-hidden"
              >
                <div className="h-full rounded-full bg-[--color-primary]" style={{ width: `${resume.pourcentage}%` }} />
              </div>
              <span className="w-10 text-right text-xs text-slate-500 tabular-nums">{resume.pourcentage} %</span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500 tabular-nums">
              {formaterMontant(resume.preleve, locale)} {t('mesAvances.preleves')}
            </p>
          </>
        ) : (
          '—'
        )}
      </Cellule>

      <Cellule libelle={t('mesAvances.colonnes.reste')} className="md:text-right">
        <span className="text-sm font-semibold text-slate-900 tabular-nums">{formaterMontant(resume.reste, locale)}</span>
      </Cellule>

      <Cellule libelle={t('mesAvances.colonnes.prochaine')} className="md:pl-4">
        {resume.prochaine ? (
          <>
            <span className="block text-sm text-slate-900 capitalize">{formaterMois(resume.prochaine.mois, locale)}</span>
            <span className="block text-xs text-slate-500 tabular-nums">{formaterMontant(resume.prochaine.montant, locale)}</span>
          </>
        ) : (
          <span className="text-sm text-slate-500">{t('mesAvances.solde')}</span>
        )}
      </Cellule>

      {/* Sur téléphone, sur la ligne du titre : trois étages au lieu de cinq. */}
      <div role="cell" className="col-start-2 row-start-1 md:col-start-auto md:row-start-auto flex items-start md:items-center justify-end">
        <button
          type="button"
          data-testid={`${prefixe}-voir-${demande.id}`}
          onClick={() => onVoir(demande)}
          className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium text-[--color-primary] hover:bg-slate-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[--color-primary]"
        >
          {t('mesAvances.voirDetail')}
          <FaChevronRight className="w-3 h-3" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function TableauRemboursements({
  demandes,
  locale,
  prefixe = 'mes-avances',
  titre = 'mesAvances.echeancierTitre',
  vide = 'mesAvances.echeancierVide',
  afficherDemandeur = false,
  outils = false,
}) {
  const { t } = useTranslation();
  const [recherche, setRecherche] = useState('');
  const [type, setType] = useState('tous');
  const [tri, setTri] = useState('echeance');
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState(null);

  const toutes = useMemo(() => avecRemboursement(demandes), [demandes]);

  const lignes = useMemo(() => {
    const terme = normaliser(recherche.trim());
    const filtrees = toutes
      .filter((demande) => type === 'tous' || demande.type === type)
      .filter((demande) => !terme || normaliser(demande.demandeur?.nom).includes(terme));
    const cle = {
      echeance: (demande) => resumer(demande).prochaine?.mois || '9999-99',
      reste: (demande) => -(resumer(demande).reste || 0),
      nom: (demande) => normaliser(demande.demandeur?.nom),
    }[tri];
    return [...filtrees].sort((a, b) => {
      const [ka, kb] = [cle(a), cle(b)];
      if (ka < kb) return -1;
      if (ka > kb) return 1;
      return normaliser(a.demandeur?.nom).localeCompare(normaliser(b.demandeur?.nom));
    });
  }, [toutes, recherche, type, tri]);

  const pages = Math.max(1, Math.ceil(lignes.length / PAR_PAGE));
  const pageCourante = Math.min(page, pages);
  const affichees = outils ? lignes.slice((pageCourante - 1) * PAR_PAGE, pageCourante * PAR_PAGE) : lignes;
  const encours = lignes.reduce((total, demande) => total + (resumer(demande).reste || 0), 0);

  // Changer ce qu'on regarde ramène à la première page : rester en page 3
  // d'une liste qui n'en a plus qu'une afficherait un tableau vide.
  const surChangement = (appliquer) => (valeur) => {
    appliquer(valeur);
    setPage(1);
  };

  return (
    <section
      aria-labelledby={`${prefixe}-titre-remboursements`}
      className="bg-white rounded-xl border border-slate-200 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 border-b border-slate-200">
        <h2 id={`${prefixe}-titre-remboursements`} className="text-sm sm:text-base font-semibold text-slate-900">
          {t(titre)}
          <span className="ml-2 text-sm font-normal text-slate-500 tabular-nums">{toutes.length}</span>
        </h2>

        {outils && toutes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <span className="sr-only">{t('mesAvances.outils.recherche')}</span>
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" aria-hidden="true" />
              <input
                type="search"
                data-testid={`${prefixe}-remboursements-recherche`}
                value={recherche}
                onChange={(e) => surChangement(setRecherche)(e.target.value)}
                placeholder={t('mesAvances.outils.recherche')}
                className="w-48 rounded-lg border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
              />
            </label>
            <div role="group" aria-label={t('mesAvances.outils.type')} className="inline-flex rounded-lg border border-slate-300 p-0.5">
              {TYPES.map((code) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`${prefixe}-remboursements-type-${code}`}
                  aria-pressed={type === code}
                  onClick={() => surChangement(setType)(code)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors ${
                    type === code ? 'bg-[--color-primary] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {t(`mesAvances.outils.types.${code}`)}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-xs text-slate-500">
              {t('mesAvances.outils.tri')}
              <select
                data-testid={`${prefixe}-remboursements-tri`}
                value={tri}
                onChange={(e) => surChangement(setTri)(e.target.value)}
                className="rounded-lg border border-slate-300 py-1.5 pl-2 pr-7 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
              >
                {TRIS.map((code) => (
                  <option key={code} value={code}>{t(`mesAvances.outils.tris.${code}`)}</option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      {toutes.length === 0 ? (
        <p data-testid={`${prefixe}-echeancier-vide`} className="px-5 py-10 text-center text-sm text-slate-500">
          {t(vide)}
        </p>
      ) : lignes.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-500">{t('mesAvances.outils.aucunResultat')}</p>
      ) : (
        <div role="table" aria-label={t(titre)}>
          <div
            role="row"
            className={`hidden md:grid gap-x-4 px-5 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 ${GRILLE}`}
          >
            <div role="columnheader">{t(afficherDemandeur ? 'mesAvances.colonnes.salarie' : 'mesAvances.colonnes.demande')}</div>
            <div role="columnheader" className="px-2">{t('mesAvances.colonnes.progression')}</div>
            <div role="columnheader" className="text-right">{t('mesAvances.colonnes.reste')}</div>
            <div role="columnheader" className="pl-4">{t('mesAvances.colonnes.prochaine')}</div>
            <div role="columnheader"><span className="sr-only">{t('mesAvances.voirDetail')}</span></div>
          </div>
          <div role="rowgroup" className="divide-y divide-slate-100">
            {affichees.map((demande) => (
              <LigneRemboursement
                key={demande.id}
                demande={demande}
                locale={locale}
                prefixe={prefixe}
                afficherDemandeur={afficherDemandeur}
                onVoir={setSelection}
              />
            ))}
          </div>
        </div>
      )}

      {outils && lignes.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 border-t border-slate-200 text-sm">
          <p data-testid={`${prefixe}-remboursements-total`} className="text-slate-600">
            {t('mesAvances.outils.encours')}{' '}
            <span className="font-semibold text-slate-900 tabular-nums">{formaterMontant(encours, locale)}</span>
          </p>
          <div className="flex items-center gap-3">
            <span data-testid={`${prefixe}-pagination`} className="text-slate-500 tabular-nums">
              {t('mesAvances.outils.plage', {
                debut: (pageCourante - 1) * PAR_PAGE + 1,
                fin: Math.min(pageCourante * PAR_PAGE, lignes.length),
                total: lignes.length,
              })}
            </span>
            <div className="inline-flex gap-1">
              <button
                type="button"
                data-testid={`${prefixe}-page-precedente`}
                disabled={pageCourante === 1}
                onClick={() => setPage(pageCourante - 1)}
                className="rounded-lg border border-slate-300 px-2.5 py-1 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                {t('mesAvances.outils.precedente')}
              </button>
              <button
                type="button"
                data-testid={`${prefixe}-page-suivante`}
                disabled={pageCourante === pages}
                onClick={() => setPage(pageCourante + 1)}
                className="rounded-lg border border-slate-300 px-2.5 py-1 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                {t('mesAvances.outils.suivante')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Drawer isOpen={Boolean(selection)} setIsOpen={(ouvert) => !ouvert && setSelection(null)} width={520}>
        {selection && (
          <DetailRemboursement
            demande={selection}
            locale={locale}
            prefixe={prefixe}
            afficherDemandeur={afficherDemandeur}
            onFermer={() => setSelection(null)}
          />
        )}
      </Drawer>
    </section>
  );
}

export default TableauRemboursements;
